import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { ApplicationPathConstants, HoldingsComponentDCR } from '@ddt-web/config';
import { InjectConfig, ModelsExperienceConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { AuthenticationManageService } from '@ddt-web/core';
import { CellData, ComponentData, DateFormatter } from '@ddt-web/data-service';
import { SharedApplicationConstants } from '@ddt-web/domain';
import { DateUtil } from '@ddt-web/generic-utils';
import { ApplicationStateStoreProxyService, PerformanceState } from '@ddt-web/shared-stores';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as get from 'lodash/get';
import * as groupBy from 'lodash/groupBy';
import * as isEmpty from 'lodash/isEmpty';

import { ModelsExperienceFilters } from '../../../models';
import { ModelCellData } from '../../../models/model-family.interface';
import { ModelsBrowseFilterPipe } from '../../../pipes/models-browse-filter.pipe';
import { ModelsBrowseLinkActionsService } from '../../../services';
import { SearchKeywordUtil } from '../../../utils/search-text-filter.util';

/* eslint-disable @nrwl/nx/enforce-module-boundaries */
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
@UntilDestroy()
@Component({
	selector: 'mb-model-list',
	templateUrl: './model-list.component.html',
	styleUrls: ['./model-list.component.scss']
})
export class ModelListComponent implements OnChanges {
	@InjectConfig(ModelsExperienceConfigKey.SHOW_MODELS_COMPARISON_CHECKBOX) public readonly showModelsComparisonStats: boolean = true;
	@InjectConfig(ModelsExperienceConfigKey.SHOW_RISK_INDICATOR) public readonly showRiskIndicator: boolean = true;
	@InjectConfig(ModelsExperienceConfigKey.SHOW_ANALYSIS_BUTTON) public readonly showAnalysisButton: boolean = true;
	@InjectConfig(ModelsExperienceConfigKey.RISK_PROFILE_KEYS) public readonly riskProfileHeaderKeys: string[] = [];
	@InjectConfig(ModelsExperienceConfigKey.SHOW_FAMILY_DESCRIPTION) public readonly showFamilyDescription: boolean = false;
	@InjectConfig(ModelsExperienceConfigKey.DATE_FORMAT) private readonly dateFormat: string = DateUtil.MODELS_US_DATE_FORMAT;
	public RISK_PROFILE_SORT_ORDER: string[] = ['conservative', 'moderate_conservative', 'moderate', 'moderate_aggressive', 'aggressive'];

	@Input() public data: ComponentData;
	@Input() public category: string;
	@Input() public groupId: string;
	@Input() public familyType: string;
	@Input() public familyId: string;
	@Input() public description: string;
	@Input() public modelIdsList: string[];
	@Input() public selectedFilters: Array<ModelsExperienceFilters> = [];
	@Input() public highlightedFilters: Array<string> = [];
	@Input() public selectedModelsToCompare: Array<{ [key: string]: CellData }> = [];
	@Input() public isAuthenticated: boolean = false;
	@Input() public searchText: string = '';

	@Output() public readonly selectedModelsChanged: EventEmitter<{
		rowData: { [key: string]: CellData };
		checked: boolean;
	}> = new EventEmitter<{ rowData: { [key: string]: CellData }; checked: boolean }>();
	@InjectConfig(ModelsExperienceConfigKey.RISK_PROFILE_SORT_ORDER) private readonly riskProfileSortOrder: string[] = [
		'conservative', 'moderate conservative', 'moderate', 'moderate aggressive', 'aggressive'
	];
	@InjectConfig(SharedConfigKey.SHOULD_USE_DAS_PORTFOLIO_ID) public readonly shouldUseDasPortfolioId: boolean = true;


	public tableData: Array<{ [key: string]: CellData }>;
	public overviewData: Array<{ [key: string]: CellData }>;
	public modelData: Array<{ [key: string]: CellData }>;

	public overviewCustomNetReturnData: Array<{ [key: string]: CellData }>;

	public riskBasedModelGroups: Map<string, Array<ModelCellData[]>>;
	public holdingsComponentDCR = HoldingsComponentDCR;

	public mainColumnWidth: string = 'dz-col-6 dz-col-sm-6';
	public columnWidth: string = 'dz-col-4 dz-col-sm-4';
	public headerWidth: string = 'dz-col-4 dz-col-sm-4';
	public customAdvisoryFee: number;
	public isCustomNetAvailable: boolean = false;
	public selectedModelIds: Array<string> = [];
	public riskProfileTooltip = ['conservative'];
	public tealiumConstants = TealiumConstants;
	public sharedApplicationConstants = SharedApplicationConstants;
	public riskProfileKeys: string[] = [];
	public dateFormatter = new DateFormatter(this.dateFormat);

	constructor(
		private filterPipe: ModelsBrowseFilterPipe,
		private modelsBrowseLinkActionsService: ModelsBrowseLinkActionsService,
		private tealiumAnalyticsService: TealiumAnalyticsService,
		public applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		public authManagerService: AuthenticationManageService

	) {
		this.applicationStateStoreProxyService.getPerformanceState()
			.pipe(untilDestroyed(this))
			.subscribe((state: PerformanceState) => {
				if (this.customAdvisoryFee !== state.customAdvisoryFee) {
					this.customAdvisoryFee = state.customAdvisoryFee;
					if (!!this.data) {
						this.prepareModelsTableData();
					}
				}
			});
		this.RISK_PROFILE_SORT_ORDER = this.riskProfileSortOrder;
		this.riskProfileKeys = this.riskProfileHeaderKeys;
	}

	public ngOnChanges(): void {
		this.modelData = get(this.data.getTabById('model.list'), 'tableData[0].rows', []);
		this.overviewData = get(this.data.getTabById('overview'), 'tableData[0].rows', []);

		if (this.isAuthenticated) {
			this.tableData = this.overviewData;
			this.overviewCustomNetReturnData = get(this.data.getTabById('overview.custom.net.return'), 'tableData[0].rows', []);
		} else {
			this.tableData = this.modelData;
		}

		this.selectedFilters = this.selectedFilters.filter((filter) => filter.applicability === 'portfolio');
		this.prepareModelsTableData();
		this.setSelectedModelIds();
	}

	private setSelectedModelIds(): void {
		this.selectedModelIds = [];
		this.selectedModelsToCompare.forEach(item => {
			this.selectedModelIds.push(item?.rowData?.['id']?.toString() || item?.['modelId']);
		});
	}

	/**
	 * merges the data of two different tabs and groups them according to their risk profile
	 */
	private prepareModelsTableData(): void {
		let listOfModels = get(this.data.getTabById('model.list'), 'tableData[0].rows', []);
		// filter if modelIdsList is defined
		listOfModels = this.modelIdsList ? listOfModels.filter(model => this.modelIdsList.includes(model.portfolioId.formattedValue)) : listOfModels;
		listOfModels = this.filterPipe.transform(listOfModels, this.selectedFilters);
		listOfModels = SearchKeywordUtil.searchByKeyword(this.searchText, listOfModels, 'description');
		const modelsOverviewData = this.tableData.map(overviewDataItem => {
			const portfolioId = this.isAuthenticated ? get(overviewDataItem, 'id.formattedValue') : get(overviewDataItem, 'portfolioId.formattedValue');
			const family = listOfModels.find(modelListElement => portfolioId === get(modelListElement, 'portfolioId.formattedValue'));
			if (family) {
				const resultObj = Object.assign({}, overviewDataItem, {
					'description': get(family, 'description'),
					'riskProfile': get(family, 'riskProfile'),
					'familyName': get(family, 'familyName'),
					'modelShortName': get(family, 'modelShortName'),
					'familyId': get(family, 'familyId'),
					'dasModelOrder': get(family, 'dasModelOrder'),
					'groupId': this.groupId,
					'modelExternalName': get(family, 'modelExternalName'),
					'modelFamilyExternalName': get(family, 'modelFamilyExternalName'),
					'id': get(family, 'portfolioId'),
					'dasPortfolioId': get(family, 'dasPortfolioId')
				});

				if (this.riskProfileKeys.includes('customNetYearToDate')) {
					this.isCustomNetAvailable = !isEmpty(this.overviewCustomNetReturnData) && !!this.customAdvisoryFee;
					if (this.isCustomNetAvailable) {
						const customNetReturnRowWithMatchId = this.overviewCustomNetReturnData.find(row => row?.id?.rawValue === overviewDataItem?.id?.rawValue);
						Object.assign(resultObj, customNetReturnRowWithMatchId);
					} else {
						this.riskProfileKeys = this.riskProfileKeys.filter(key => key !== 'customNetYearToDate');
					}
				}
				return resultObj;
			}
			return null;
		})
			.filter(data => data)
			.sort((a, b) => get(a, 'dasModelOrder.rawValue', 0) - get(b, 'dasModelOrder.rawValue', 0));
		this.updateColumnWidth();
		const groupedData = groupBy(modelsOverviewData, data => get(data, 'riskProfile.rawValue'));
		this.riskBasedModelGroups = groupedData;
	}

	public redirectTo360(portfolioId: string): void {
		const event = TealiumConstants.MODELS_EXPERIENCE_EVENTS.MODELS_EXPERIENCE_APP_NAME + ':' + TealiumConstants.MODELS_EXPERIENCE_EVENTS.FIND_AND_COMPARE_PAGE + `:model header click:${this.familyType}:${this.category}:${this.familyId}:${portfolioId}`;
		this.tealiumAnalyticsService.trackEventTealium(
			new TealiumAnalyticsEvent(event, { toolName: TealiumConstants.TOOL_NAMES.MODELS_EXPERIENCE })
		);
		this.modelsBrowseLinkActionsService.redirectOnLinkAction(SharedApplicationConstants.PORTFOLIO_ANALYSER_PAGE, ApplicationPathConstants.PA_PATHS.OVERVIEW, portfolioId);
	}

	private updateColumnWidth(): void {
		const isRiskAndAnalyzeColPresent = this.showRiskIndicator && this.showAnalysisButton;

		switch (this.riskProfileKeys.length) {
			case 4:
				this.mainColumnWidth = 'dz-col-lg-6 dz-col-md-24';
				this.columnWidth = 'dz-col-lg-4 dz-col-md-6';
				this.headerWidth = 'dz-col-lg-4 dz-col-md-24';
				break;
			case 5:
				this.mainColumnWidth = `${isRiskAndAnalyzeColPresent ? 'dz-col-lg-7 dz-col-md-24' : 'dz-col-lg-3 dz-col-md-24'}`;
				this.columnWidth = 'dz-col-lg-3 width-20';
				this.headerWidth = 'dz-col-lg-3 dz-col-md-24';
				break;
			case 6:
				this.mainColumnWidth = `${isRiskAndAnalyzeColPresent ? 'dz-col-lg-6 dz-col-md-24' : 'dz-col-lg-3 dz-col-md-24'}`;
				this.columnWidth = 'dz-col-lg-3 width-20';
				this.headerWidth = 'dz-col-lg-3 dz-col-md-24';
				break;
			default:
				break;
		}
	}

	public authenticateUser(): void {
		this.authManagerService.initializeOktaSignIn({ 'redirectUrlPath': '/' + ApplicationPathConstants.MODELS_EXPERIENCE_PATHS.FIND_AND_COMPARE, 'familyId': this.familyId });
	}

}
